import type * as React from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Badge, { type BadgeProps } from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Link from '@cloudscape-design/components/link';
import Table from '@cloudscape-design/components/table';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { formatDateTime } from '@/lib/format';
import type { Refund, RefundStatus } from '@etiquetachef/types';

type Props = {
  refunds: Refund[];
  locale: string;
};

const STATUS_COLOR: Record<string, BadgeProps['color']> = {
  PENDING: 'blue',
  SUCCEEDED: 'green',
  FAILED: 'red',
};

const REASON_COLOR: Record<string, BadgeProps['color']> = {
  MBG: 'blue',
  REQUESTED_BY_SUPPORT: 'blue',
  DUPLICATE: 'grey',
  FRAUDULENT: 'red',
  DISPUTE: 'severity-high',
  COMMERCIAL: 'green',
  OTHER: 'grey',
};

/**
 * Tabela de reembolsos (cross-tenant em `/finance/refunds`, ou de um tenant na aba de
 * histórico): data, tenant, pagamento, valor, motivo, status e gateway, com links para o
 * tenant e a fatura.
 *
 * Origem: sem issue — commit 973be8d9c, 2026-05-05 12:08 +0200 (Sprint 5 PR H).
 *
 * Contrato: só apresentação de `Refund[]`. `amount` em CENTAVOS — `MoneyDisplay` divide. Cores
 * de badge por `status`/`reason` com fallback `grey` para valor desconhecido (enum novo no
 * schema não quebra a tela). O link do pagamento aponta para a FATURA
 * (`/billing/invoices/{tenant}/{invoice_id}`) — não existe página de pagamento.
 *
 * Usado por: `routes/RefundsPage.tsx`, `features/finance/TenantFinanceTab.tsx` (web
 * `apps/admin`).
 *
 * Dados: `tenants/{tenantId}/refunds/{refundId}` — `RefundSchema` em
 * `packages/types/src/schemas.ts` (`tenant_id`, `payment_id`, `invoice_id`, `amount`,
 * `currency`, `reason`, `status`, `provider`, `requested_at` ms).
 *
 * Histórico:
 *  - 2026-06-08 22:54 +0200 — (39-F) migração para Cloudscape `Table`.
 */
export function RefundsTable({ refunds, locale }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  /** Link Cloudscape que navega pelo React Router (sem recarregar). */
  const navLink = (href: string, label: string): React.JSX.Element => (
    <Link
      href={href}
      onFollow={(e): void => {
        e.preventDefault();
        void navigate(href);
      }}
    >
      <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{label}</span>
    </Link>
  );

  return (
    <Table<Refund>
      items={refunds}
      trackBy="id"
      variant="embedded"
      empty={<Box textAlign="center" color="inherit">{t('admin.finance.refunds.empty')}</Box>}
      columnDefinitions={[
        {
          id: 'requestedAt',
          header: t('admin.finance.refunds.columns.requestedAt'),
          cell: (r): React.ReactNode => formatDateTime(r.requested_at, locale),
        },
        {
          id: 'tenant',
          header: t('admin.finance.refunds.columns.tenant'),
          cell: (r): React.ReactNode => navLink(`/tenants/${r.tenant_id}`, r.tenant_id),
        },
        {
          id: 'payment',
          header: t('admin.finance.refunds.columns.payment'),
          cell: (r): React.ReactNode =>
            navLink(`/billing/invoices/${r.tenant_id}/${r.invoice_id}`, r.payment_id),
        },
        {
          id: 'amount',
          header: t('admin.finance.refunds.columns.amount'),
          cell: (r): React.ReactNode => (
            <MoneyDisplay amount={r.amount} currency={r.currency} locale={locale} />
          ),
        },
        {
          id: 'reason',
          header: t('admin.finance.refunds.columns.reason'),
          cell: (r): React.ReactNode => (
            <Badge color={REASON_COLOR[r.reason] ?? 'grey'}>
              {t(`admin.finance.refunds.reason.${r.reason}`)}
            </Badge>
          ),
        },
        {
          id: 'status',
          header: t('admin.finance.refunds.columns.status'),
          cell: (r): React.ReactNode => (
            <Badge color={STATUS_COLOR[r.status as RefundStatus] ?? 'grey'}>
              {t(`admin.finance.refunds.status.${r.status}`)}
            </Badge>
          ),
        },
        {
          id: 'provider',
          header: t('admin.finance.refunds.columns.provider'),
          cell: (r): React.ReactNode => (
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.provider}</span>
          ),
        },
      ]}
    />
  );
}
